<script lang="ts" setup>
import { onMounted, ref } from 'vue';

import {
  useBrowserStore,
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import {
  AppSwiperGallery,
  AppRouterLink,
  AppButton,
  AppRawButton,
} from '@libs/ui';

import type {
  AccommodationProductCardData,
  AccommodationSegmentContext,
} from '../../../models';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import AlternativeSuggestionHeader from '../AlternativeSuggestionHeader.vue';
import DateCardTag from '../DateCardTag.vue';
import TripOverviewTrashIconButton from '../../TripOverviewTrashIconButton.vue';

import AlternativeSuggestionProductCardLayout from './AlternativeSuggestionProductCardLayout.vue';
import AccommodationAlternativeProductCardContent from './AccommodationAlternativeProductCardContent.vue';

interface AccommodationAlternativeProductCardProps {
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
  shouldHideProductLink: boolean;
}

const props = defineProps<AccommodationAlternativeProductCardProps>();

interface AccommodationProductCardEmits {
  (event: 'confirm-alternative'): void;
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
  (event: 'navigation-triggered'): void;
  (event: 'open-hotel-selection'): void;
  (event: 'remove-accommodation'): void;
}

const emit = defineEmits<AccommodationProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();
const browserStore = useBrowserStore();

onMounted(() => {
  statsigEventsStore.logTripOverviewAlternativeRoomsDisplayed();
});

const enableHoverColor = ref<boolean>(false);

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewAlternativeAccommodationGalleryImageSwipe();
}

function openHotelSelection(): void {
  statsigEventsStore.logExperimentAccommodationActionClicked(
    'changeButtonExperiment'
  );
  emit('open-hotel-selection');
}

function removeAccommodation(): void {
  statsigEventsStore.logExperimentIngredientRemoved('accommodation');
  emit('remove-accommodation');
}

function showDetailsExperiment(): void {
  if (props.shouldHideProductLink) {
    return;
  }
  showDetails('alternativeCard');
}

function showDetails(
  source:
    | 'alternativeCard'
    | 'detailsButtonExperiment'
    | 'detailsButtonAlternativeMobile'
): void {
  statsigEventsStore.logExperimentAccommodationActionClicked(source);
  emit('show-details');
}
</script>

<template>
  <div class="accommodation-alternative-product-card">
    <AlternativeSuggestionProductCardLayout
      v-if="props.shouldHideProductLink"
      :enable-hover-color="enableHoverColor"
      :should-hide-product-link="props.shouldHideProductLink"
      class="experiment"
    >
      <template #header>
        <AlternativeSuggestionHeader
          :info-text="
            $t('planner.no_availability.messages.alternative_selected_room')
          "
          @confirm="emit('confirm-alternative')"
        >
          <template #secondary-button>
            <AppRouterLink
              :link="
                routerStore.getHotelSelectionUrl({
                  ingredientIndex: props.context.ingredientIndex,
                })
              "
              font="font-link-s-light"
              color="color-neutral-70"
              @navigation-triggered="emit('navigation-triggered')"
            >
              {{ $t('planner.see_other_hotels') }}
            </AppRouterLink>
          </template>
        </AlternativeSuggestionHeader>
      </template>

      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'accommodation-alternative-product-card-swiper-gallery',
              })
          "
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          class="gallery"
          @click="showDetailsExperiment"
          @gallery-image-swipe="onGalleryImageSwipe"
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
        />
      </template>

      <template #content>
        <AccommodationAlternativeProductCardContent
          class="alternative-product-card-content"
          :product-card-content="props.productCardData.productCardContent"
          :context="props.context"
          :should-hide-product-link="props.shouldHideProductLink"
          @click="showDetailsExperiment"
          @show-rooms="emit('show-rooms')"
          @mouse-enter-content="enableHoverColor = true"
          @mouse-leave-content="enableHoverColor = false"
        />
      </template>
      <template #footer>
        <div class="button-wrapper">
          <AppRawButton
            class="details"
            @click="showDetails('detailsButtonExperiment')"
          >
            {{ $t('planner.details') }}
          </AppRawButton>
          <AppButton
            state="enabled"
            type="primary"
            size="small"
            :ariaLabel="$t('a11y.change_accommodation')"
            @click="openHotelSelection"
          >
            {{ $t('buttons.stay') }}
          </AppButton>
        </div>
      </template>
      <template #tag>
        <DateCardTag class="date-tag" :date="props.productCardData.date" />
      </template>
      <template #trash-icon>
        <TripOverviewTrashIconButton
          class="trash-icon"
          :with-border="browserStore.isSmallerThan600px"
          :rounded="false"
          :ariaLabel="$t('a11y.remove_stay')"
          @remove="removeAccommodation"
        />
      </template>
    </AlternativeSuggestionProductCardLayout>
    <ImgProductCardLayout v-else :enable-hover-color="enableHoverColor">
      <template #header>
        <AlternativeSuggestionHeader
          :info-text="
            $t('planner.no_availability.messages.alternative_selected_room')
          "
          @confirm="emit('confirm-alternative')"
        >
          <template #secondary-button>
            <AppRouterLink
              :link="
                routerStore.getHotelSelectionUrl({
                  ingredientIndex: props.context.ingredientIndex,
                })
              "
              font="font-link-s-light"
              color="color-neutral-70"
              @navigation-triggered="emit('navigation-triggered')"
            >
              {{ $t('planner.see_other_hotels') }}
            </AppRouterLink>
          </template>
        </AlternativeSuggestionHeader>
      </template>

      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'accommodation-alternative-product-card-swiper-gallery',
              })
          "
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          class="gallery"
          @click="showDetailsExperiment"
          @gallery-image-swipe="onGalleryImageSwipe"
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
        />
      </template>

      <template #content>
        <AccommodationAlternativeProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          :context="props.context"
          :should-hide-product-link="props.shouldHideProductLink"
          @show-details="showDetailsExperiment"
          @show-rooms="emit('show-rooms')"
          @mouse-enter-content="enableHoverColor = true"
          @mouse-leave-content="enableHoverColor = false"
        />
      </template>
      <template #footer>
        <ProductCardFooter
          @mouse-enter="enableHoverColor = true"
          @mouse-leave="enableHoverColor = false"
          @show-details="showDetails('detailsButtonAlternativeMobile')"
        />
      </template>
    </ImgProductCardLayout>
  </div>
</template>

<style lang="scss" scoped>
.accommodation-alternative-product-card {
  .experiment {
    @include for-more-or-equal-than-600-px {
      .alternative-product-card-content {
        // TODO: REFACTOR THIS LOGIC AFTER remove_product_links EXPERIMENT
        margin-top: calc(var(--spacing-16) * -1);
        margin-right: calc(var(--spacing-16) * -1);
      }

      .date-tag {
        top: var(--spacing-16);
        left: var(--spacing-16);
        border-top-left-radius: var(--spacing-8);
      }
    }

    .trash-icon {
      position: absolute;
      top: var(--spacing-16);
      right: var(--spacing-16);

      @include for-less-or-equal-than-599-px {
        top: 0;
        right: 0;
      }
    }

    .button-wrapper {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      gap: var(--spacing-16);

      .details {
        @include font-label-m-light;
        text-decoration: underline;
        cursor: pointer;
      }

      @include for-less-or-equal-than-599-px {
        padding-left: var(--spacing-16);
        padding-right: var(--spacing-16);
      }
    }
  }

  .gallery {
    cursor: pointer;
  }
}
</style>
